import { AccessDenied } from '@/components/layout/access-denied'
import { AuthorizationUnavailable } from '@/components/layout/authorization-unavailable'
import {
  type RouteAccessId,
  canAccessRoute,
  isParticipantRouteAccessId,
} from '@/config/route-access'
import { useAccessControl } from '@/hooks/use-access-control'
import { useAuth } from '@/hooks/use-auth'
import { firstAllowedParticipantRoute } from '@/lib/access-policy'
import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

export function AccessGuard({
  routeId,
  children,
  deniedTitle,
  deniedMessage,
}: {
  routeId: RouteAccessId
  children?: ReactNode
  deniedTitle?: string
  deniedMessage?: string
}) {
  const { isLoading: isIdentityLoading, isAuthenticated, user } = useAuth()
  const authorization = useAccessControl(isAuthenticated)
  const location = useLocation()

  if (isIdentityLoading || (isAuthenticated && authorization.isPending)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />

  if (authorization.isError || !authorization.data) {
    return <AuthorizationUnavailable retry={() => void authorization.refetch()} />
  }

  if (!canAccessRoute(authorization.data, routeId, user?.organization_id)) {
    if (isParticipantRouteAccessId(routeId)) {
      const participantLanding = firstAllowedParticipantRoute(authorization.data)
      if (participantLanding && participantLanding !== location.pathname) {
        return <Navigate to={participantLanding} replace />
      }
    }
    return (
      <AccessDenied
        {...(deniedTitle ? { title: deniedTitle } : {})}
        {...(deniedMessage ? { message: deniedMessage } : {})}
      />
    )
  }

  return children ?? <Outlet />
}
